import { c as _c } from "react/compiler-runtime";
import CarouselComponent, { CarouselType } from '@spa/components/carousel/carousel.component';
import { ProductMedium, isAiGeneratedProductMedia, isProductImageMedium, isProductVideoMedium } from '@spa/components/product-info/product-media/product-medium';
import React, { CSSProperties, ReactElement, RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import useMediaSwipe, { MediaSwipe, Slide } from './useMediaSwipe';
import { CmsAiMarkerStyle } from '@spa/generated/graphql/graphql';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import ImageSectionComponent from '@spa/components/image-section/image-section.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import VideoSectionComponent from '@spa/components/video-section/video-section.component';
import classNames from 'clsx';
import { setToOriginal } from '../../utils';
import styles from './product-media-expanded.component.module.scss';
import useBodyOverflowHidden from '@spa/lib/react/hooks/useBodyOverflowHidden';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
export interface Props {
  readonly id: string;
  readonly media: ReadonlyArray<ProductMedium>;
  readonly productName: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly currentMainMedia: number;
  readonly setCurrentMainMedia: (imageIndex: number) => void;
  readonly returnFocusRef?: RefObject<HTMLElement | null>;
}
interface Coordinates {
  readonly x: number;
  readonly y: number;
}
interface Box {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}
interface Pinch {
  readonly distance: number;
  readonly scale: number;
  readonly center: Coordinates;
}
interface Gesture {
  readonly pointers: Map<number, Coordinates>;
  pointerDownPos: Coordinates | null;
  pointerDownTime: number;
  dragStartPosition: Coordinates;
  dragging: boolean;
  pinch: Pinch | null;
}
interface ZoomState {
  readonly position: Coordinates;
  readonly zoomScale: number;
}
type NavigationDirection = 'prev' | 'next';
const ZOOM_SCALE = 2.5;
const MIN_ZOOMABLE_SCALE = 1.25;
const MAX_PINCH_SCALE = 4;
const DRAG_THRESHOLD = 5;
const KEYBOARD_PAN_STEP = 40;
const IMAGE_SIZES = '100vw';
const ZOOMED_IMAGE_SIZES = `${ZOOM_SCALE * 100}vw`;
const INITIAL_POSITION: Coordinates = {
  x: 0,
  y: 0
};
const INITIAL_ZOOM: ZoomState = {
  position: INITIAL_POSITION,
  zoomScale: 1
};
const FOCUSABLE_SELECTOR = 'button:not([disabled]):not([tabindex="-1"]), [href], [tabindex]:not([tabindex="-1"])';
const getFitBox = (wrapper: HTMLDivElement, image: ProductMedium | undefined): Box | undefined => {
  let result: Box | undefined;
  if (image && isProductImageMedium(image) && image.width && image.height) {
    const {
      left,
      top,
      width,
      height
    } = wrapper.getBoundingClientRect();
    const fitScale: number = Math.min(width / image.width, height / image.height);
    const fitWidth: number = image.width * fitScale;
    const fitHeight: number = image.height * fitScale;
    result = {
      left: left + (width - fitWidth) / 2,
      top: top + (height - fitHeight) / 2,
      width: fitWidth,
      height: fitHeight
    };
  }
  return result;
};
const getZoomScale = (wrapper: HTMLDivElement, image: ProductMedium | undefined): number => {
  const fitBox: Box | undefined = getFitBox(wrapper, image);
  let result: number = 1;
  if (fitBox && fitBox.width > 0 && image && isProductImageMedium(image) && image.width) {
    const scale: number = Math.min(ZOOM_SCALE, image.width / fitBox.width);
    if (scale >= MIN_ZOOMABLE_SCALE) {
      result = scale;
    }
  }
  return result;
};
const getPanLimits = (wrapper: HTMLDivElement, fitBox: Box, scale: number): Coordinates => {
  const {
    width,
    height
  } = wrapper.getBoundingClientRect();
  return {
    x: Math.max(0, (fitBox.width * scale - width) / 2),
    y: Math.max(0, (fitBox.height * scale - height) / 2)
  };
};
const clampPosition = (position: Coordinates, limits: Coordinates): Coordinates => ({
  x: Math.round(Math.min(Math.max(position.x, -limits.x), limits.x)),
  y: Math.round(Math.min(Math.max(position.y, -limits.y), limits.y))
});
const getOffsetFromCenter = (clientX: number, clientY: number, fitBox: Box): Coordinates => ({
  x: clientX - (fitBox.left + fitBox.width / 2),
  y: clientY - (fitBox.top + fitBox.height / 2)
});
const getPointerDistance = (pointers: ReadonlyArray<Coordinates>): number => Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y);
const getPointerCenter = (pointers: ReadonlyArray<Coordinates>): Coordinates => ({
  x: (pointers[0].x + pointers[1].x) / 2,
  y: (pointers[0].y + pointers[1].y) / 2
});
interface SlideContentProps {
  readonly item: ProductMedium;
  readonly productName: string;
  readonly isCurrent: boolean;
  readonly imageTransform: string;
  readonly isTransforming: boolean;
  readonly highResImageUrl: string | null;
  readonly loadedHighResImageUrl: string | null;
  readonly onHighResImageLoad: (url: string) => void;
}
function SlideContent(t0) {
  const $ = _c(40);
  const {
    item,
    productName,
    isCurrent,
    imageTransform,
    isTransforming,
    highResImageUrl,
    loadedHighResImageUrl,
    onHighResImageLoad
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("product-info");
  const {
    formatMessage: formatAiMarkerMessage
  } = useNamespacedIntl("image-ai-marker");
  if (!isProductImageMedium(item)) {
    let t1;
    if ($[0] !== isCurrent || $[1] !== item) {
      t1 = isCurrent ? <video className={styles["product-media-expanded__video"]} autoPlay={true} controls={true} src={item.url} /> : null;
      $[0] = isCurrent;
      $[1] = item;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  const {
    url,
    width,
    height
  } = item;
  let t1;
  if ($[3] !== item) {
    t1 = isAiGeneratedProductMedia(item);
    $[3] = item;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isAiImage = t1;
  let t2;
  if ($[5] !== formatMessage || $[6] !== productName) {
    t2 = formatMessage("imageAlt", {
      productName
    });
    $[5] = formatMessage;
    $[6] = productName;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const imageAlt = t2;
  let t3;
  if ($[8] !== formatAiMarkerMessage || $[9] !== imageAlt || $[10] !== isAiImage) {
    t3 = isAiImage ? `${imageAlt}, ${formatAiMarkerMessage("accessibleLabel")}` : imageAlt;
    $[8] = formatAiMarkerMessage;
    $[9] = imageAlt;
    $[10] = isAiImage;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const displayAlt = t3;
  const t4 = isCurrent ? imageTransform : "scale(1)";
  let t5;
  if ($[12] !== t4) {
    t5 = {
      transform: t4
    };
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const slideImageStyle = t5;
  const hasHighResImage = isCurrent && highResImageUrl === url;
  const isHighResImageLoaded = hasHighResImage && loadedHighResImageUrl === url;
  let t6;
  let t7;
  if ($[14] !== displayAlt || $[15] !== hasHighResImage || $[16] !== height || $[17] !== isAiImage || $[18] !== isCurrent || $[19] !== isHighResImageLoaded || $[20] !== isTransforming || $[21] !== item.digitalSourceType || $[22] !== onHighResImageLoad || $[23] !== slideImageStyle || $[24] !== url || $[25] !== width) {
    const imageClassName = classNames(styles["product-media-expanded__image"], {
      [styles["product-media-expanded__image--no-transition"]]: isCurrent && isTransforming
    });
    const t8 = classNames(imageClassName, {
      [styles["product-media-expanded__image--hidden"]]: isHighResImageLoaded
    });
    let t9;
    if ($[28] !== url) {
      t9 = setToOriginal(url);
      $[28] = url;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t10;
    if ($[30] !== isAiImage || $[31] !== item.digitalSourceType) {
      t10 = isAiImage ? {
        "data-digital-source-type": item.digitalSourceType
      } : {};
      $[30] = isAiImage;
      $[31] = item.digitalSourceType;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    t6 = <ImageComponent className={t8} alt={isCurrent ? displayAlt : ""} src={t9} priority={false} loading="eager" width={width} height={height} sizes={IMAGE_SIZES} style={slideImageStyle} {...t10} />;
    t7 = hasHighResImage && <ImageComponent className={classNames(imageClassName, styles["product-media-expanded__image--high-res"], {
      [styles["product-media-expanded__image--hidden"]]: !isHighResImageLoaded
    })} alt="" src={setToOriginal(url)} priority={false} loading="eager" width={width} height={height} sizes={ZOOMED_IMAGE_SIZES} style={slideImageStyle} onLoad={() => onHighResImageLoad(url)} />;
    $[14] = displayAlt;
    $[15] = hasHighResImage;
    $[16] = height;
    $[17] = isAiImage;
    $[18] = isCurrent;
    $[19] = isHighResImageLoaded;
    $[20] = isTransforming;
    $[21] = item.digitalSourceType;
    $[22] = onHighResImageLoad;
    $[23] = slideImageStyle;
    $[24] = url;
    $[25] = width;
    $[26] = t6;
    $[27] = t7;
  } else {
    t6 = $[26];
    t7 = $[27];
  }
  let t8;
  if ($[33] !== imageAlt || $[34] !== isAiImage) {
    t8 = isAiImage && <ImageAiMarkerComponent style={CmsAiMarkerStyle.WHITE_ON_BLACK} alt={imageAlt} />;
    $[33] = imageAlt;
    $[34] = isAiImage;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t9 = <>{t6}{t7}{t8}</>;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
interface NavigationButtonProps {
  readonly direction: NavigationDirection;
  readonly targetIndex: number;
  readonly mediaCount: number;
  readonly onNavigate: (imageIndex: number) => void;
}
function NavigationButton(t0) {
  const $ = _c(17);
  const {
    direction,
    targetIndex,
    mediaCount,
    onNavigate
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("product-info");
  const isHidden = targetIndex < 0 || targetIndex >= mediaCount;
  const t1 = styles[`product-media-expanded__nav-button--${direction}`];
  let t2;
  if ($[0] !== isHidden || $[1] !== t1) {
    t2 = classNames(styles["product-media-expanded__nav-button"], t1, {
      [styles["product-media-expanded__nav-button--hidden"]]: isHidden
    });
    $[0] = isHidden;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isHidden ? -1 : undefined;
  let t4;
  if ($[3] !== onNavigate || $[4] !== targetIndex) {
    t4 = () => onNavigate(targetIndex);
    $[3] = onNavigate;
    $[4] = targetIndex;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `${direction}Image`;
  let t6;
  if ($[6] !== formatMessage || $[7] !== t5) {
    t6 = formatMessage(t5);
    $[6] = formatMessage;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = styles[`product-media-expanded__nav-icon--${direction}`];
  let t8;
  if ($[9] !== t7) {
    t8 = <ImageComponent className={t7} src="/assets/icons/arrow_forward.svg" alt="" width={20} height={20} />;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t6 || $[15] !== t8) {
    t9 = <button type="button" className={t2} tabIndex={t3} onClick={t4} aria-label={t6}>{t8}</button>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function ProductMediaExpanded(t0) {
  const $ = _c(113);
  const {
    id,
    media,
    productName,
    open,
    onClose,
    currentMainMedia,
    setCurrentMainMedia,
    returnFocusRef
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("product-info");
  const expandedImageWrapperRef = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      pointers: new Map(),
      pointerDownPos: null,
      pointerDownTime: 0,
      dragStartPosition: INITIAL_POSITION,
      dragging: false,
      pinch: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const gestureRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...INITIAL_ZOOM,
      isZoomable: false,
      isTransforming: false,
      highResImageUrl: null as string | null,
      loadedHighResImageUrl: null as string | null
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [state, updateState] = useObjectReducer(t2);
  const {
    highResImageUrl,
    isTransforming,
    isZoomable,
    loadedHighResImageUrl,
    position,
    zoomScale
  } = state;
  const isZoomed = zoomScale > 1;
  useBodyOverflowHidden(open);
  const currentImage = media[currentMainMedia];
  let t3;
  if ($[2] !== updateState) {
    t3 = () => {
      updateState(INITIAL_ZOOM);
    };
    $[2] = updateState;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resetZoom = t3;
  let t4;
  if ($[4] !== currentMainMedia || $[5] !== media.length || $[6] !== resetZoom || $[7] !== setCurrentMainMedia || $[8] !== updateState) {
    t4 = imageIndex => {
      if (imageIndex >= 0 && imageIndex < media.length && imageIndex !== currentMainMedia) {
        setCurrentMainMedia(imageIndex);
        resetZoom();
        updateState({
          highResImageUrl: null,
          loadedHighResImageUrl: null
        });
      }
    };
    $[4] = currentMainMedia;
    $[5] = media.length;
    $[6] = resetZoom;
    $[7] = setCurrentMainMedia;
    $[8] = updateState;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const goToImage = t4;
  let t5;
  if ($[10] !== currentMainMedia || $[11] !== goToImage || $[12] !== media) {
    t5 = {
      media,
      currentMainMedia,
      goToImage
    };
    $[10] = currentMainMedia;
    $[11] = goToImage;
    $[12] = media;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const swipe = useMediaSwipe(t5);
  let t6;
  if ($[14] !== currentImage || $[15] !== updateState) {
    t6 = (deltaX, deltaY) => {
      const wrapper = expandedImageWrapperRef.current;
      const fitBox = wrapper ? getFitBox(wrapper, currentImage) : undefined;
      if (wrapper && fitBox) {
        updateState(prev => ({
          position: clampPosition({
            x: prev.position.x + deltaX,
            y: prev.position.y + deltaY
          }, getPanLimits(wrapper, fitBox, prev.zoomScale))
        }));
      }
    };
    $[14] = currentImage;
    $[15] = updateState;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const panBy = t6;
  let t7;
  if ($[17] !== onClose || $[18] !== resetZoom || $[19] !== returnFocusRef) {
    t7 = () => {
      resetZoom();
      returnFocusRef?.current?.focus({
        preventScroll: true
      });
      onClose();
    };
    $[17] = onClose;
    $[18] = resetZoom;
    $[19] = returnFocusRef;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const closeDialog = t7;
  let t8;
  let t9;
  if ($[21] !== closeDialog || $[22] !== currentMainMedia || $[23] !== goToImage || $[24] !== isZoomed || $[25] !== open || $[26] !== panBy) {
    t8 = () => {
      const panByKey = {
        ArrowLeft: {
          x: KEYBOARD_PAN_STEP,
          y: 0
        },
        ArrowRight: {
          x: -KEYBOARD_PAN_STEP,
          y: 0
        },
        ArrowUp: {
          x: 0,
          y: KEYBOARD_PAN_STEP
        },
        ArrowDown: {
          x: 0,
          y: -KEYBOARD_PAN_STEP
        }
      };
      const handleKeyDown = function handleKeyDown(e) {
        if (open) {
          if (e.key === "Escape") {
            closeDialog();
          } else {
            if (!(e.target instanceof HTMLVideoElement)) {
              if (isZoomed && panByKey[e.key]) {
                e.preventDefault();
                panBy(panByKey[e.key].x, panByKey[e.key].y);
              } else {
                if (e.key === "ArrowLeft") {
                  goToImage(currentMainMedia - 1);
                } else {
                  if (e.key === "ArrowRight") {
                    goToImage(currentMainMedia + 1);
                  }
                }
              }
            }
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t9 = [closeDialog, currentMainMedia, goToImage, isZoomed, open, panBy];
    $[21] = closeDialog;
    $[22] = currentMainMedia;
    $[23] = goToImage;
    $[24] = isZoomed;
    $[25] = open;
    $[26] = panBy;
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== open) {
    t10 = () => {
      if (!open) {
        return;
      }
      const dialog = dialogRef.current;
      const previousActiveElement = document.activeElement as HTMLElement | null;
      closeButtonRef.current?.focus();
      return () => {
        const activeElement = document.activeElement;
        if (!activeElement || activeElement === document.body || dialog?.contains(activeElement)) {
          previousActiveElement?.focus({
            preventScroll: true
          });
        }
      };
    };
    t11 = [open];
    $[29] = open;
    $[30] = t10;
    $[31] = t11;
  } else {
    t10 = $[30];
    t11 = $[31];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[32] !== open) {
    t12 = () => {
      const trapFocus = function trapFocus(event) {
        const dialog_0 = dialogRef.current;
        if (dialog_0 && event.key === "Tab") {
          const focusableElements = Array.from(dialog_0.querySelectorAll(FOCUSABLE_SELECTOR)).filter(_temp);
          const firstElement = focusableElements[0];
          const lastElement = focusableElements.at(-1);
          if (firstElement && lastElement) {
            if (event.shiftKey && document.activeElement === firstElement) {
              lastElement.focus();
              event.preventDefault();
            } else {
              if (!event.shiftKey && document.activeElement === lastElement) {
                firstElement.focus();
                event.preventDefault();
              }
            }
          }
        }
      };
      if (open) {
        document.addEventListener("keydown", trapFocus);
      }
      return () => {
        document.removeEventListener("keydown", trapFocus);
      };
    };
    t13 = [open];
    $[32] = open;
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[35] !== currentImage || $[36] !== open || $[37] !== updateState) {
    t14 = () => {
      const wrapper_0 = expandedImageWrapperRef.current;
      let resizeObserver;
      if (wrapper_0 && open) {
        const updateZoomability = () => {
          updateState({
            isZoomable: getZoomScale(wrapper_0, currentImage) > 1
          });
        };
        updateZoomability();
        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => {
            updateZoomability();
          });
          resizeObserver.observe(wrapper_0);
        }
      } else {
        updateState({
          isZoomable: false
        });
      }
      return () => {
        resizeObserver?.disconnect();
      };
    };
    t15 = [currentImage, open, updateState];
    $[35] = currentImage;
    $[36] = open;
    $[37] = updateState;
    $[38] = t14;
    $[39] = t15;
  } else {
    t14 = $[38];
    t15 = $[39];
  }
  useLayoutEffect(t14, t15);
  let t16;
  if ($[40] !== currentImage || $[41] !== updateState) {
    t16 = (clientX, clientY, wrapper_1) => {
      const scale = getZoomScale(wrapper_1, currentImage);
      const fitBox_0 = getFitBox(wrapper_1, currentImage);
      if (scale > 1 && fitBox_0) {
        const offset = getOffsetFromCenter(clientX, clientY, fitBox_0);
        updateState({
          position: clampPosition({
            x: offset.x * (1 - scale),
            y: offset.y * (1 - scale)
          }, getPanLimits(wrapper_1, fitBox_0, scale)),
          zoomScale: scale,
          highResImageUrl: currentImage?.url ?? null
        });
      }
    };
    $[40] = currentImage;
    $[41] = updateState;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const zoomIntoPoint = t16;
  let t17;
  if ($[43] !== currentImage || $[44] !== updateState) {
    t17 = (clientX_0, clientY_0, wrapper_2) => {
      const fitBox_1 = getFitBox(wrapper_2, currentImage);
      if (fitBox_1) {
        const {
          left,
          top,
          width,
          height
        } = wrapper_2.getBoundingClientRect();
        updateState(prev_0 => {
          const limits = getPanLimits(wrapper_2, fitBox_1, prev_0.zoomScale);
          return {
            position: clampPosition({
              x: limits.x * (1 - 2 * (clientX_0 - left) / width),
              y: limits.y * (1 - 2 * (clientY_0 - top) / height)
            }, limits)
          };
        });
      }
    };
    $[43] = currentImage;
    $[44] = updateState;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const followMouse = t17;
  let t18;
  if ($[46] !== currentImage || $[47] !== updateState) {
    t18 = (pinch, wrapper_3) => {
      const fitBox_2 = getFitBox(wrapper_3, currentImage);
      const pointers = Array.from(gestureRef.current.pointers.values());
      if (fitBox_2 && pointers.length === 2) {
        const scale_0 = Math.min(Math.max(pinch.scale * getPointerDistance(pointers) / pinch.distance, 1), MAX_PINCH_SCALE);
        const center = getOffsetFromCenter(pinch.center.x, pinch.center.y, fitBox_2);
        updateState(prev_1 => {
          const ratio = scale_0 / prev_1.zoomScale;
          return {
            position: clampPosition({
              x: center.x - (center.x - prev_1.position.x) * ratio,
              y: center.y - (center.y - prev_1.position.y) * ratio
            }, getPanLimits(wrapper_3, fitBox_2, scale_0)),
            zoomScale: scale_0,
            highResImageUrl: currentImage?.url ?? null
          };
        });
      }
    };
    $[46] = currentImage;
    $[47] = updateState;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const pinchZoom = t18;
  let t19;
  if ($[49] !== currentImage || $[50] !== updateState) {
    t19 = (clientX_1, clientY_1, wrapper_4) => {
      const fitBox_3 = getFitBox(wrapper_4, currentImage);
      const {
        pointerDownPos,
        dragStartPosition
      } = gestureRef.current;
      if (pointerDownPos && fitBox_3) {
        updateState(prev_2 => ({
          position: clampPosition({
            x: dragStartPosition.x + clientX_1 - pointerDownPos.x,
            y: dragStartPosition.y + clientY_1 - pointerDownPos.y
          }, getPanLimits(wrapper_4, fitBox_3, prev_2.zoomScale))
        }));
      }
    };
    $[49] = currentImage;
    $[50] = updateState;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const dragImage = t19;
  let t20;
  if ($[52] !== position || $[53] !== swipe || $[54] !== updateState || $[55] !== zoomScale) {
    t20 = event_0 => {
      const gesture = gestureRef.current;
      if (event_0.pointerType === "touch") {
        event_0.preventDefault();
        gesture.pointers.set(event_0.pointerId, {
          x: event_0.clientX,
          y: event_0.clientY
        });
        if (gesture.pointers.size === 2) {
          const pointers_0 = Array.from(gesture.pointers.values());
          gesture.dragging = true;
          gesture.pinch = {
            distance: getPointerDistance(pointers_0),
            scale: zoomScale,
            center: getPointerCenter(pointers_0)
          };
          updateState({
            isTransforming: true
          });
          if (swipe.isActive()) {
            swipe.stop();
          }
        } else {
          gesture.pointerDownPos = {
            x: event_0.clientX,
            y: event_0.clientY
          };
          gesture.pointerDownTime = event_0.timeStamp;
          gesture.dragStartPosition = position;
        }
      }
    };
    $[52] = position;
    $[53] = swipe;
    $[54] = updateState;
    $[55] = zoomScale;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const handlePointerDown = t20;
  let t21;
  if ($[57] !== dragImage || $[58] !== followMouse || $[59] !== isZoomed || $[60] !== pinchZoom || $[61] !== swipe || $[62] !== updateState) {
    t21 = event_1 => {
      const wrapper_5 = expandedImageWrapperRef.current;
      const gesture_0 = gestureRef.current;
      if (wrapper_5) {
        event_1.preventDefault();
        const {
          clientX: clientX_2,
          clientY: clientY_2,
          pointerType
        } = event_1;
        if (gesture_0.pointers.has(event_1.pointerId)) {
          gesture_0.pointers.set(event_1.pointerId, {
            x: clientX_2,
            y: clientY_2
          });
        }
        if (pointerType === "touch") {
          if (gesture_0.pinch) {
            pinchZoom(gesture_0.pinch, wrapper_5);
          } else {
            if (gesture_0.pointerDownPos) {
              const deltaX_0 = clientX_2 - gesture_0.pointerDownPos.x;
              const deltaY_0 = clientY_2 - gesture_0.pointerDownPos.y;
              if (isZoomed) {
                if (!gesture_0.dragging && Math.hypot(deltaX_0, deltaY_0) > DRAG_THRESHOLD) {
                  gesture_0.dragging = true;
                  updateState({
                    isTransforming: true
                  });
                }
                if (gesture_0.dragging) {
                  dragImage(clientX_2, clientY_2, wrapper_5);
                }
              } else {
                if (swipe.isActive() || Math.abs(deltaX_0) > DRAG_THRESHOLD && Math.abs(deltaX_0) > Math.abs(deltaY_0)) {
                  swipe.track(deltaX_0);
                }
              }
            }
          }
        } else {
          if (pointerType === "mouse" && isZoomed) {
            followMouse(clientX_2, clientY_2, wrapper_5);
          }
        }
      }
    };
    $[57] = dragImage;
    $[58] = followMouse;
    $[59] = isZoomed;
    $[60] = pinchZoom;
    $[61] = swipe;
    $[62] = updateState;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  const handlePointerMove = t21;
  let t22;
  if ($[64] !== isZoomed || $[65] !== resetZoom || $[66] !== swipe || $[67] !== zoomIntoPoint) {
    t22 = (pointerDownPos_0, releasePos, duration, wasSwiping) => {
      const wrapper_6 = expandedImageWrapperRef.current;
      if (isZoomed) {
        resetZoom();
        return;
      }
      if (wrapper_6) {
        const deltaX_1 = releasePos.x - pointerDownPos_0.x;
        const deltaY_1 = releasePos.y - pointerDownPos_0.y;
        const wrapperWidth = wrapper_6.getBoundingClientRect().width;
        const hasSwiped = swipe.release(deltaX_1, deltaY_1, wrapperWidth, duration);
        const isTap = !wasSwiping && Math.hypot(deltaX_1, deltaY_1) <= DRAG_THRESHOLD;
        if (!hasSwiped && isTap) {
          zoomIntoPoint(releasePos.x, releasePos.y, wrapper_6);
        }
      }
    };
    $[64] = isZoomed;
    $[65] = resetZoom;
    $[66] = swipe;
    $[67] = zoomIntoPoint;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const handleTouchRelease = t22;
  let t23;
  if ($[69] !== handleTouchRelease || $[70] !== isZoomed || $[71] !== resetZoom || $[72] !== swipe || $[73] !== updateState || $[74] !== zoomIntoPoint) {
    t23 = event_2 => {
      event_2.preventDefault();
      const wrapper_7 = expandedImageWrapperRef.current;
      const gesture_1 = gestureRef.current;
      const wasSwiping_0 = swipe.isActive();
      const {
        dragging,
        pointerDownPos: pointerDownPos_1,
        pointerDownTime
      } = gesture_1;
      gesture_1.pointers.delete(event_2.pointerId);
      if (gesture_1.pinch) {
        if (gesture_1.pointers.size === 0) {
          gesture_1.pinch = null;
          gesture_1.dragging = false;
          gesture_1.pointerDownPos = null;
          updateState(_temp2);
        }
      } else {
        gesture_1.dragging = false;
        gesture_1.pointerDownPos = null;
        if (wasSwiping_0) {
          swipe.stop();
        }
        if (dragging) {
          updateState({
            isTransforming: false
          });
        }
        if (!dragging && event_2.type !== "pointercancel") {
          if (event_2.pointerType === "touch") {
            if (pointerDownPos_1) {
              handleTouchRelease(pointerDownPos_1, {
                x: event_2.clientX,
                y: event_2.clientY
              }, event_2.timeStamp - pointerDownTime, wasSwiping_0);
            }
          } else {
            if (isZoomed) {
              resetZoom();
            } else {
              if (wrapper_7) {
                zoomIntoPoint(event_2.clientX, event_2.clientY, wrapper_7);
              }
            }
          }
        }
      }
    };
    $[69] = handleTouchRelease;
    $[70] = isZoomed;
    $[71] = resetZoom;
    $[72] = swipe;
    $[73] = updateState;
    $[74] = zoomIntoPoint;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  const handlePointerUp = t23;
  let t24;
  if ($[76] !== isZoomed || $[77] !== resetZoom || $[78] !== zoomIntoPoint) {
    t24 = event_3 => {
      const wrapper_8 = expandedImageWrapperRef.current;
      if (wrapper_8 && (event_3.key === "Enter" || event_3.key === " ")) {
        event_3.preventDefault();
        if (isZoomed) {
          resetZoom();
        } else {
          const {
            left: left_0,
            top: top_0,
            width: width_0,
            height: height_0
          } = wrapper_8.getBoundingClientRect();
          zoomIntoPoint(left_0 + width_0 / 2, top_0 + height_0 / 2, wrapper_8);
        }
      }
    };
    $[76] = isZoomed;
    $[77] = resetZoom;
    $[78] = zoomIntoPoint;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  const handleWrapperKeyDown = t24;
  let t25;
  if ($[80] !== isZoomable || $[81] !== isZoomed) {
    t25 = isZoomable ? {
      cursor: isZoomed ? "zoom-out" : "zoom-in"
    } : undefined;
    $[80] = isZoomable;
    $[81] = isZoomed;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  const imageStyle = t25;
  const imageTransform = isZoomed ? `${position.x || position.y ? `translate(${position.x}px, ${position.y}px) ` : ""}scale(${zoomScale})` : "scale(1)";
  let t26;
  if ($[83] !== currentImage || $[84] !== formatMessage || $[85] !== handleWrapperKeyDown || $[86] !== isZoomable || $[87] !== isZoomed) {
    t26 = currentImage && isProductImageMedium(currentImage) ? {
      role: "button",
      tabIndex: 0,
      "aria-label": formatMessage("zoomImage"),
      "aria-pressed": isZoomed,
      "aria-disabled": !isZoomable,
      onKeyDown: handleWrapperKeyDown
    } as const : {};
    $[83] = currentImage;
    $[84] = formatMessage;
    $[85] = handleWrapperKeyDown;
    $[86] = isZoomable;
    $[87] = isZoomed;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  const zoomButtonProps = t26;
  let t27;
  if ($[89] !== updateState) {
    t27 = url => {
      updateState({
        loadedHighResImageUrl: url
      });
    };
    $[89] = updateState;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  const handleHighResImageLoad = t27;
  let t28;
  if ($[91] !== closeDialog || $[92] !== currentMainMedia || $[93] !== formatMessage || $[94] !== goToImage || $[95] !== handleHighResImageLoad || $[96] !== handlePointerDown || $[97] !== handlePointerMove || $[98] !== handlePointerUp || $[99] !== highResImageUrl || $[100] !== id || $[101] !== imageStyle || $[102] !== imageTransform || $[103] !== isTransforming || $[104] !== loadedHighResImageUrl || $[105] !== media || $[106] !== open || $[107] !== productName || $[108] !== swipe || $[109] !== zoomButtonProps) {
    t28 = open && media.length !== 0 && <dialog className={styles["product-media-expanded"]} ref={dialogRef} open={true} aria-modal="true" aria-label={formatMessage("expandedViewLabel")} data-testid="zoom-in"><div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{media.length > 1 && formatMessage("carouselImageAlt", {
          productName,
          index: currentMainMedia + 1,
          length: media.length
        })}</div><div className={styles["product-media-expanded__close"]}><button ref={closeButtonRef} className={styles["product-media-expanded__close_btn"]} onClick={closeDialog} aria-label={formatMessage("closeExpandedView")} data-testid="zoom-in-close-btn"><ImageComponent className={styles["product-media-expanded__close_icon"]} src="/assets/icons/close-icon.svg" alt="" width={24} height={24} /></button></div><div className={styles["product-media-expanded__media"]}><div className={styles["product-media-expanded__image-wrapper"]} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} style={imageStyle} ref={expandedImageWrapperRef} data-testid="product-media-expanded" {...zoomButtonProps}>{swipe.visibleSlides.map(t29 => {
            const {
              item,
              index
            } = t29;
            return <div key={item.url} className={classNames(styles["product-media-expanded__slide"], {
              [styles["product-media-expanded__slide--animated"]]: !swipe.isSwiping
            })} style={{
              transform: swipe.getSlideTransform(index)
            }} aria-hidden={index === currentMainMedia ? undefined : true}><SlideContent item={item} productName={productName} isCurrent={index === currentMainMedia} imageTransform={imageTransform} isTransforming={isTransforming} highResImageUrl={highResImageUrl} loadedHighResImageUrl={loadedHighResImageUrl} onHighResImageLoad={handleHighResImageLoad} /></div>;
          })}</div>{media.length > 1 && <NavigationButton direction="prev" targetIndex={currentMainMedia - 1} mediaCount={media.length} onNavigate={goToImage} />}{media.length > 1 && <NavigationButton direction="next" targetIndex={currentMainMedia + 1} mediaCount={media.length} onNavigate={goToImage} />}</div>{media && media.length > 1 && <div className={styles["product-media-expanded__carousel-wrapper"]}><CarouselComponent id={`${id}-carousel`} carouselType={CarouselType.MEDIA} startIndex={currentMainMedia}>{media.map((medium, imageIndex_0) => {
            const tileClasses = classNames(styles["product-media-expanded__tile"], {
              [styles["product-media-expanded__tile--selected"]]: currentMainMedia === imageIndex_0
            });
            return <button type="button" key={medium.url} className={tileClasses} aria-pressed={currentMainMedia === imageIndex_0} onClick={() => goToImage(imageIndex_0)}>{isProductVideoMedium(medium) ? <VideoSectionComponent url={medium.url} /> : <ImageSectionComponent alt={formatMessage("carouselImageAlt", {
                productName,
                index: imageIndex_0 + 1,
                length: media.length
              })} url={setToOriginal(medium.url)} priority={false} sizes="(max-width: 768px) 50px, (max-width: 1024px) 62px, 74px" />}</button>;
          })}</CarouselComponent></div>}</dialog>;
    $[91] = closeDialog;
    $[92] = currentMainMedia;
    $[93] = formatMessage;
    $[94] = goToImage;
    $[95] = handleHighResImageLoad;
    $[96] = handlePointerDown;
    $[97] = handlePointerMove;
    $[98] = handlePointerUp;
    $[99] = highResImageUrl;
    $[100] = id;
    $[101] = imageStyle;
    $[102] = imageTransform;
    $[103] = isTransforming;
    $[104] = loadedHighResImageUrl;
    $[105] = media;
    $[106] = open;
    $[107] = productName;
    $[108] = swipe;
    $[109] = zoomButtonProps;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== t28) {
    t29 = <>{t28}</>;
    $[111] = t28;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  return t29;
}
function _temp2(prev_3) {
  return {
    ...(prev_3.zoomScale <= 1 ? INITIAL_ZOOM : {}),
    isTransforming: false
  };
}
function _temp(element) {
  return getComputedStyle(element).display !== "none";
}
export default ProductMediaExpanded;